/**
 * Autocrypt header parsing — RFC 4880-style OpenPGP keys advertised in
 * a single email header so future replies can be encrypted without
 * out-of-band key exchange.
 *
 * Header shape (Autocrypt Level 1, draft-autocrypt-lk2017-01):
 *  Autocrypt: addr=alice@example.com; prefer-encrypt=mutual;
 *  keydata=<base64-of-key-payload>
 *
 * We surface the structured fields. Cryptographic verification is out
 * of scope (no DNS, no key-server lookups — local-first).
 */

import type { ParsedHeaders } from "../email-parser";

/** Parsed Autocrypt header — public-key advertisement for opportunistic encryption. */
export interface AutocryptInfo {
  addr: string;
  /** Sender's stated encryption preference (mutual / nopreference / …). */
  preferEncrypt?: string;
  /** Base64 OpenPGP key payload, exactly as advertised. */
  keydata: string;
  /** Approximate key bytes after base64 decode (informational). */
  keydataBytes: number;
  /** Raw header value, kept for debug / copy-to-clipboard. */
  raw: string;
}

/**
 * Pull the `Autocrypt:` header off a parsed message into structured
 * fields (addr, prefer-encrypt, keydata). Call when the Security tab
 * wants to surface "this sender is advertising a PGP key" without
 * verifying it (verification is out of scope — local-only).
 */
export function parseAutocrypt(headers: ParsedHeaders): AutocryptInfo | null {
  const raw = headers.raw["autocrypt"];
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const fields = parseAutocryptAttributes(trimmed);
  const addr = fields["addr"];
  const keydata = fields["keydata"];
  if (!addr || !keydata) return null;
  // base64 → bytes is 3/4 of the input length, ignoring padding.
  const padded = keydata.replace(/[^A-Za-z0-9+/=]/g, "");
  const keydataBytes = Math.floor((padded.length * 3) / 4);
  return {
    addr,
    preferEncrypt: fields["prefer-encrypt"],
    keydata,
    keydataBytes,
    raw: trimmed,
  };
}

/**
 * Attribute split for an Autocrypt-style header. Whitespace inside
 * `keydata` doesn't matter for the base64 payload, so we strip it as
 * we go (the real-world value is folded across many continuation
 * lines).
 */
function parseAutocryptAttributes(value: string): Record<string, string> {
  const out: Record<string, string> = {};
  const parts = value.split(";");
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    const key = part.slice(0, eq).trim().toLowerCase();
    const val = part.slice(eq + 1).trim();
    if (!key) continue;
    out[key] = key === "keydata" ? val.replace(/\s+/g, "") : val;
  }
  return out;
}
